import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import { TrendingUp, ShoppingBag, Store, Package, Loader2, CreditCard, AlertTriangle, CheckCircle2, XCircle, Radio } from "lucide-react";

export default function Overview() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [lowStock, setLowStock] = useState(null);
  const [live, setLive] = useState(null);
  const { selectedStore, currentStore } = useStore();

  useEffect(() => {
    setData(null);
    const q = selectedStore && selectedStore !== "all" ? `?store_id=${selectedStore}` : "";
    api.get(`/admin/analytics${q}`).then((r) => setData(r.data));
    const sq = selectedStore && selectedStore !== "all" ? `&store_id=${selectedStore}` : "";
    api.get(`/admin/low-stock?threshold=5${sq}`).then((r) => setLowStock(r.data)).catch(() => setLowStock([]));
  }, [selectedStore]);

  useEffect(() => {
    api.get("/admin/payments/health").then((r) => setHealth(r.data)).catch(() => setHealth({ ok: false }));
  }, []);

  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/admin/analytics/live").then((r) => { if (alive) setLive(r.data); }).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!data) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;
  }

  const scopeLabel = selectedStore === "all" || !selectedStore ? "All stores" : (currentStore?.name || "Store");

  const stats = [
    { label: "Revenue", value: money(data.total_revenue), icon: TrendingUp },
    { label: "Orders", value: data.total_orders, icon: ShoppingBag },
    { label: "Stores", value: data.stores_count, icon: Store },
    { label: "Products", value: data.products_count, icon: Package },
  ];
  const barColors = ["#0095f6", "#dd2a7b", "#8134af", "#f58529", "#515bd4", "#10b981"];

  return (
    <div className="animate-fade-up" data-testid="overview-page">
      <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Dashboard · {scopeLabel}</span>
      <h1 className="font-heading font-light text-4xl tracking-tight mt-1 mb-8">Overview</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="adm-card bg-white border border-zinc-200/80 rounded-xl p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center justify-between text-zinc-400">
              <span className="text-xs font-heading font-bold uppercase tracking-[0.15em]">{s.label}</span>
              <s.icon size={18} strokeWidth={1.5} />
            </div>
            <div className="font-mono-data text-4xl mt-3 text-zinc-900">{s.value}</div>
          </div>
        ))}
      </div>

      {live && (
        <div className="bg-white border border-zinc-200/80 rounded-xl p-5 mb-6" data-testid="live-visitors">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className={`absolute inline-flex h-full w-full rounded-full ${live.active > 0 ? "bg-emerald-400 animate-ping" : "bg-zinc-300"} opacity-75`} />
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${live.active > 0 ? "bg-emerald-500" : "bg-zinc-300"}`} />
              </span>
              <h2 className="font-heading font-medium text-lg">Live visitors</h2>
              <span className="text-xs text-zinc-400">· last 30 min</span>
            </div>
            <div className="flex items-center gap-5 text-right">
              <div><div className="font-heading font-light text-2xl leading-none" data-testid="live-active">{live.active}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">Active</div></div>
              <div><div className="font-heading font-light text-2xl leading-none" data-testid="live-views">{live.views}</div><div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">Views</div></div>
            </div>
          </div>
          {live.recent.length === 0 ? (
            <p className="text-sm text-zinc-400 flex items-center gap-2"><Radio size={14} /> No visitors in the last 30 minutes.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {live.recent.map((v, i) => (
                <span key={i} data-testid={`live-visitor-${i}`} className="inline-flex items-center gap-1.5 bg-zinc-50 border border-zinc-200 rounded-full pl-1.5 pr-3 py-1 text-xs">
                  <span className="text-sm leading-none">{v.flag || "🌐"}</span>
                  <span className="text-zinc-600">{[v.city, v.country_code].filter(Boolean).join(", ") || "Unknown"}</span>
                  <span className="text-zinc-300">·</span>
                  <span className="text-zinc-400 truncate max-w-[130px]">{v.path}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-zinc-200/80 rounded-xl p-6">
          <h2 className="font-heading font-medium text-lg mb-4">Revenue · last 14 days</h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.trend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} width={40} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7", fontFamily: "Manrope" }} />
              <Area type="monotone" dataKey="revenue" stroke="#4F46E5" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200/80 rounded-xl p-6">
          <h2 className="font-heading font-medium text-lg mb-4">Revenue by store</h2>
          <div className="space-y-4">
            {data.per_store.map((s, i) => (
              <div key={s.store_id} data-testid={`store-revenue-${i}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-body font-semibold truncate">{s.name}</span>
                  <span className="font-heading">{money(s.revenue)}</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${data.per_store[0]?.revenue ? (s.revenue / data.per_store[0].revenue) * 100 : 0}%`,
                    background: barColors[i % barColors.length],
                  }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-zinc-200/80 rounded-xl p-6">
          <h2 className="font-heading font-medium text-lg mb-4">Top products</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="title" width={110} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e4e4e7" }} />
              <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                {data.top_products.map((_, i) => <Cell key={i} fill={barColors[i % barColors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200/80 rounded-xl p-6">
          <h2 className="font-heading font-medium text-lg mb-4">Recent orders</h2>
          <div className="space-y-3">
            {data.recent_orders.map((o) => (
              <div key={o.id} className="flex justify-between items-center text-sm" data-testid={`recent-order-${o.id}`}>
                <div className="min-w-0">
                  <div className="font-body font-semibold truncate">{o.items[0]?.title}</div>
                  <div className="text-xs text-zinc-400">@{o.store_handle}</div>
                </div>
                <span className="font-heading font-semibold">{money(o.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <PaymentsHealth health={health} />
        <LowStock items={lowStock} />
      </div>
    </div>
  );
}

const PaymentsHealth = ({ health }) => {
  const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "never");
  const payoutDate = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString() : "—");
  return (
    <div className="lg:col-span-2 bg-white border border-zinc-200/80 rounded-xl p-6" data-testid="payments-health">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard size={18} className="text-[#635bff]" />
        <h2 className="font-heading font-medium text-lg">Payments health</h2>
      </div>
      {!health ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-400" size={18} /></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Row label="Connection" ok={health.ok}
            value={health.ok ? (health.using_own_account ? "Your Stripe account" : "Platform account") : "Unreachable"} />
          <Row label="Mode" ok={health.mode === "live"} neutral={health.mode !== "live"}
            value={health.ok ? (health.mode === "live" ? "LIVE" : "Test") : "—"} />
          <Row label="Charges enabled" ok={!!health.charges_enabled} value={health.charges_enabled ? "Yes" : "No"} />
          <Row label="Payouts enabled" ok={!!health.payouts_enabled} value={health.payouts_enabled ? "Yes" : "No"} />
          <Row label="Webhook configured" ok={!!health.webhook_configured} value={health.webhook_configured ? "Yes" : "No"} />
          <Row label="Webhook last fired" ok={!!health.webhook_last_received} neutral={!health.webhook_last_received}
            value={fmtDate(health.webhook_last_received)} />
          <div className="sm:col-span-2 border-t border-zinc-100 pt-3 flex items-center justify-between">
            <span className="text-zinc-500">Last payout</span>
            <span className="font-heading font-semibold">
              {health.last_payout ? `${health.last_payout.currency?.toUpperCase()} ${health.last_payout.amount.toFixed(2)} · ${payoutDate(health.last_payout.arrival_date)}` : "No payouts yet"}
            </span>
          </div>
          {!health.ok && <div className="sm:col-span-2 text-xs text-rose-500">{health.error}</div>}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, ok, neutral }) => (
  <div className="flex items-center justify-between">
    <span className="text-zinc-500">{label}</span>
    <span className="flex items-center gap-1.5 font-heading font-semibold">
      {!neutral && (ok ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-rose-500" />)}
      {value}
    </span>
  </div>
);

const LowStock = ({ items }) => (
  <div className="bg-white border border-zinc-200/80 rounded-xl p-6" data-testid="low-stock-widget">
    <div className="flex items-center gap-2 mb-4">
      <AlertTriangle size={18} className="text-amber-500" />
      <h2 className="font-heading font-medium text-lg">Low stock</h2>
    </div>
    {!items ? (
      <div className="flex justify-center py-6"><Loader2 className="animate-spin text-zinc-400" size={18} /></div>
    ) : items.length === 0 ? (
      <p className="text-sm text-zinc-400" data-testid="low-stock-empty">All products are well stocked. 🎉</p>
    ) : (
      <div className="space-y-2.5 max-h-64 overflow-y-auto">
        {items.map((p) => (
          <div key={p.id} className="flex items-center justify-between text-sm" data-testid={`low-stock-${p.id}`}>
            <div className="min-w-0">
              <div className="font-body font-semibold truncate">{p.title}</div>
              <div className="text-xs text-zinc-400">@{p.store_handle}</div>
            </div>
            <span className={`font-heading font-bold rounded-full px-2.5 py-0.5 text-xs ${p.stock === 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
              {p.stock === 0 ? "Out" : `${p.stock} left`}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
